import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LayoutGrid, List, Search } from 'lucide-react';
import Divider from '../../../components/UI/Divider';
import { useUserContext } from '../../../contexts/UserContext';
import { getTasks, trackAnalyticsEvent } from '../../../utils/APIs';
import { ANALYTICS_EVENTS } from '../../../constants/analyticsEvents';
import Loader from '../../../components/UI/Loader/Loader';
import Dropdown from '../../../components/UI/Dropdown/Dropdown';
import FunctionCard from '../../../components/Function/FunctionCard';
import FunctionTable from '../../../components/Function/FunctionTable';
import { useContentfulContext } from '../../../contexts/ContentfulContext';
import { toast } from 'react-toastify';
import { Card, Input, SegmentedControl } from '../../../components/UI';

const Tasks = () => {
    const navigation = useNavigate();
    const { state } = useLocation();
    const { userData, appData } = useUserContext();
    const { content } = useContentfulContext();

    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [loadingAppData, setLoadingAppData] = useState(false);
    const [loadingTasks, setLoadingTasks] = useState(false);
    const [error, setError] = useState("");
    const [tasks, setTasks] = useState([]);
    const [upcomingTasks, setUpcomingTasks] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterValue, setFilterValue] = useState({ id: "available-to-all", name: "Available to All" });
    const [viewMode, setViewMode] = useState(state?.mode || "grid");

    const getAllFunctions = async () => {
        try {
            setLoadingTasks(true);

            const functionsData = await getTasks(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                userData?.selectedOrganization?.role[0].name?.toLowerCase(),
            );

            if (functionsData.status) {
                setTasks(functionsData.data.filter((task) => task.available));
                setUpcomingTasks(functionsData.data.filter((task) => !task.available));
                setError("");
            }
        } catch (err) {
            setError(err?.message || content?.COMMON_MESSAGE.something_wrong_msg);
        } finally {
            setLoadingTasks(false);
        }
    };

    const onFilterTextBoxChanged = (e) => {
        setSearchTerm(e.target.value);
    };

    const getAllAppData = async () => {
        try {
            setLoadingAppData(true);
        } catch (err) {
            console.log('error', err);
        } finally {
            setLoadingAppData(false);
        }
    };

    const handleProductSelect = (productId) => {
        if (selectedProducts.includes(productId)) {
            setSelectedProducts(selectedProducts.filter(id => id !== productId));
        } else {
            setSelectedProducts([...selectedProducts, productId]);
        }
    };

    useEffect(() => {
        if (appData?.scopes) {
            setProducts(appData.scopes);
        }
    }, [appData?.scopes]);

    useEffect(() => {
        if (!userData?.selectedOrganization) return;
        getAllAppData();
    }, [userData?.selectedOrganization]);

    useEffect(() => {
        if (!userData?.selectedOrganization) return;
        getAllFunctions();
    }, [userData?.selectedOrganization]);

    const handleFilterChange = (data) => {
        setFilterValue(data);
    };

    const handleNavigate = (taskId, task) => {
        if (!task.is_accessible_to_org && !task.is_accessible_to_user) {
            toast.error(content.TASKS_CONTENT.toast_message.message_1);
            return;
        }
        if (!task.is_accessible_to_org) {
            toast.error(content.TASKS_CONTENT.toast_message.message_2);
            return;
        }
        if (!task.is_accessible_to_user) {
            toast.error(content.TASKS_CONTENT.toast_message.message_3);
            return;
        }
        navigation(`/functions/one-off-tasks/${taskId}`, { state: { viewMode } });
    };

    const onViewChange = (mode) => {
        setViewMode(mode);
        trackAnalyticsEvent(
            userData.token,
            userData.selectedOrganization.org_token,
            ANALYTICS_EVENTS.CLICK_TASK_VIEW_MODE,
            { mode }
        );
    };

    const filteredAvailableTasks = useMemo(() => {
        return tasks.filter((task) => {
            const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesProduct =
                selectedProducts.length === 0 ||
                task.products.some((product) => selectedProducts.includes(product.id));

            let matchesAccess = true;
            if (filterValue.id === "available-to-you") {
                matchesAccess = task.is_accessible_to_user;
            } else if (filterValue.id === "available-to-organization") {
                matchesAccess = task.is_accessible_to_org;
            }

            return matchesSearch && matchesProduct && matchesAccess;
        });
    }, [tasks, searchTerm, selectedProducts, filterValue]);

    const filteredUpcomingTasks = useMemo(() => {
        return upcomingTasks.filter((task) => {
            const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesProduct =
                selectedProducts.length === 0 ||
                task.products.some((product) => selectedProducts.includes(product.id));

            return matchesSearch && matchesProduct;
        });
    }, [upcomingTasks, searchTerm, selectedProducts]);

    return (
        <div className="w-full">
            {loadingTasks && <Loader />}
            <main id="main" className="stride-page-shell flex flex-col space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <span className="stride-eyebrow">Functions / Library</span>
                        <h1 className="text-2xl font-bold tracking-tight text-stride-heading">
                            {content.TASKS_CONTENT.title}
                        </h1>
                        <p className="text-sm text-stride-muted mt-1">
                            {content.TASKS_CONTENT.description}
                        </p>
                    </div>

                    {/* Filter Dropdown */}
                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <span className="text-xs font-semibold text-stride-muted uppercase tracking-wider whitespace-nowrap">
                            {content.TASKS_CONTENT.view_by}
                        </span>
                        <div className="w-full md:min-w-[220px]">
                            <Dropdown
                                items={[
                                    { id: "available-to-you", name: "Available to you" },
                                    { id: "available-to-organization", name: "Available to organization" },
                                    { id: "available-to-all", name: "Available to all" }
                                ]}
                                callback={handleFilterChange}
                                initialValue={filterValue}
                                width="100"
                                isWidthInPercentage={true}
                                disabled={loadingAppData}
                            />
                        </div>
                    </div>
                </div>

                {/* Filter and Search Bar */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pt-2">
                    {/* Product Pills */}
                    <div className="flex items-center gap-2 overflow-x-auto pb-1 max-w-full">
                        <span className="text-xs font-semibold text-stride-muted uppercase tracking-wider shrink-0 mr-1">
                            Products:
                        </span>
                        {products.map((product) => {
                            const isSelected = selectedProducts.includes(product.id);
                            return (
                                <button
                                    key={product.id}
                                    type="button"
                                    onClick={() => handleProductSelect(product.id)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all shrink-0 border ${
                                        isSelected
                                            ? 'bg-primary text-white border-primary shadow-xs'
                                            : 'bg-white text-stride-heading border-border hover:border-border-hover'
                                    }`}
                                >
                                    <h3 className="text-xs font-semibold">{product.name}</h3>
                                </button>
                            );
                        })}
                    </div>

                    {/* Search & View Mode Switch */}
                    <div className="flex items-center gap-3 w-full md:w-auto justify-between md:justify-end">
                        <div className="w-full md:w-64">
                            <Input
                                placeholder={content.TASKS_CONTENT.search_placeholder}
                                value={searchTerm}
                                onChange={onFilterTextBoxChanged}
                                prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                                clearable
                                onClear={() => setSearchTerm('')}
                            />
                        </div>

                        {/* View Mode Pills */}
                        <SegmentedControl
                            value={viewMode}
                            onChange={onViewChange}
                            variant="primary"
                            size="md"
                            options={[
                                { value: 'grid', label: '', icon: <LayoutGrid className="w-4 h-4" />, title: 'Grid View' },
                                { value: 'table', label: '', icon: <List className="w-4 h-4" />, title: 'Table View' },
                            ]}
                            className="shrink-0"
                        />
                    </div>
                </div>

                {/* Empty State / Error */}
                {!loadingTasks && !loadingAppData && !error && filteredAvailableTasks.length === 0 && filteredUpcomingTasks.length === 0 && (
                    <Card className="text-center p-12 max-w-lg mx-auto">
                        <p className="text-base text-stride-heading font-medium">
                            {content.TASKS_CONTENT.no_data_found}
                        </p>
                    </Card>
                )}
                {!loadingTasks && error && (
                    <Card className="text-center p-8 max-w-lg mx-auto border-rose-200 bg-rose-50/20">
                        <p className="text-sm font-semibold text-rose-700">{error}</p>
                    </Card>
                )}

                {/* Table View */}
                {viewMode === "table" && (
                    <FunctionTable
                        tasks={[...filteredAvailableTasks, ...filteredUpcomingTasks]}
                        selectedProducts={selectedProducts}
                        viewMode={viewMode}
                        loading={loadingTasks || loadingAppData}
                    />
                )}

                {/* Grid View */}
                {viewMode === "grid" && (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 w-full">
                            {filteredAvailableTasks.map((task) => (
                                <div id={task.id} key={task.id}>
                                    <FunctionCard
                                        task={task}
                                        isUpcoming={false}
                                        isPremium={task?.is_premium || false}
                                        selectedProducts={selectedProducts}
                                        onTaskClick={(taskId) => handleNavigate(taskId, task)}
                                    />
                                </div>
                            ))}
                        </div>

                        {filteredUpcomingTasks.length > 0 && (
                            <>
                                <div className="pt-4">
                                    <Divider>{content.TASKS_CONTENT.upcoming_functions}</Divider>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 w-full">
                                    {filteredUpcomingTasks.map((task) => (
                                        <div id={task.id} key={task.id}>
                                            <FunctionCard
                                                task={task}
                                                isUpcoming={true}
                                                isPremium={task?.is_premium || false}
                                                selectedProducts={selectedProducts}
                                                onTaskClick={(taskId) => navigation(`/functions/one-off-tasks/${taskId}`, { state: { viewMode } })}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </>
                )}
            </main>
        </div>
    );
};

export default Tasks;
